import {
  deleteQueryKeys,
  readOptionalNumberParam,
  readTrimmedParam,
  writeOptionalNumberParam,
  writeTrimmedParam,
} from "@/lib/filterUrlParams";
import type { AvailabilityColor, PropertyFilter } from "@/types/entities";

const AVAILABILITY_COLORS: AvailabilityColor[] = ["RED", "BLUE", "BLACK"];

function readAvailabilityColor(params: URLSearchParams): AvailabilityColor | undefined {
  const value = readTrimmedParam(params, "availabilityColor")?.toUpperCase();
  if (!value) return undefined;
  return AVAILABILITY_COLORS.includes(value as AvailabilityColor)
    ? (value as AvailabilityColor)
    : undefined;
}

/** List-filter keys; `view` is handled separately on Properties. */
export const PROPERTY_FILTER_PARAM_KEYS = [
  "search",
  "market",
  "minBedrooms",
  "minPrice",
  "maxPrice",
  "availabilityColor",
  "crmSyncStatus",
] as const;

export const CLEARED_PROPERTY_FILTER: Partial<PropertyFilter> = {
  search: undefined,
  market: undefined,
  minBedrooms: undefined,
  minPrice: undefined,
  maxPrice: undefined,
  availabilityColor: undefined,
  crmSyncStatus: undefined,
};

export function parsePropertyFilterFromSearchParams(params: URLSearchParams): PropertyFilter {
  const filter: PropertyFilter = {};
  const search = readTrimmedParam(params, "search");
  if (search) filter.search = search;
  const market = readTrimmedParam(params, "market");
  if (market) filter.market = market;
  const minBedrooms = readOptionalNumberParam(params, "minBedrooms");
  if (minBedrooms !== undefined) filter.minBedrooms = minBedrooms;
  const minPrice = readOptionalNumberParam(params, "minPrice");
  if (minPrice !== undefined) filter.minPrice = minPrice;
  const maxPrice = readOptionalNumberParam(params, "maxPrice");
  if (maxPrice !== undefined) filter.maxPrice = maxPrice;
  const availabilityColor = readAvailabilityColor(params);
  if (availabilityColor) filter.availabilityColor = availabilityColor;
  const crmSyncStatus = readTrimmedParam(params, "crmSyncStatus");
  if (crmSyncStatus) filter.crmSyncStatus = crmSyncStatus;
  return filter;
}

export function applyPropertyFilterToSearchParams(
  filter: PropertyFilter,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, PROPERTY_FILTER_PARAM_KEYS);
  writeTrimmedParam(next, "search", filter.search);
  writeTrimmedParam(next, "market", filter.market);
  writeOptionalNumberParam(next, "minBedrooms", filter.minBedrooms);
  writeOptionalNumberParam(next, "minPrice", filter.minPrice);
  writeOptionalNumberParam(next, "maxPrice", filter.maxPrice);
  if (filter.availabilityColor) writeTrimmedParam(next, "availabilityColor", filter.availabilityColor);
  writeTrimmedParam(next, "crmSyncStatus", filter.crmSyncStatus);
  return next;
}

function canonicalQuery(filter: PropertyFilter): string {
  return applyPropertyFilterToSearchParams(filter, new URLSearchParams()).toString();
}

export function propertyFiltersEquivalent(a: PropertyFilter, b: PropertyFilter): boolean {
  return canonicalQuery(a) === canonicalQuery(b);
}

export const propertyFilterUrlSyncConfig = {
  parseFromSearchParams: parsePropertyFilterFromSearchParams,
  applyToSearchParams: applyPropertyFilterToSearchParams,
  filtersEquivalent: propertyFiltersEquivalent,
  clearedFilter: CLEARED_PROPERTY_FILTER,
};
